/* eslint-disable @typescript-eslint/no-explicit-any */
import { InvoiceWithLineItems, CreditCard } from '@atlassian/cssxp-types-billing';
import objectMapper from 'object-mapper';
import { isInvoiceRefunded } from '@atlassian/cssxp-utils-invoice';

const commerceAddressTransformer = {
  fullName: 'fullName',
  organization: 'organization',
  'address.address1': 'address.address1',
  'address.address2': 'address.address2',
  'address.city': 'address.city',
  'address.state': 'address.state',
  'address.country': 'address.country',
  'address.postalCode': 'address.postalCode',
  'address.phone': 'address.phone',
  taxId: 'taxId',
};

const creditCardTransformer = {
  last4Numbers: 'last4Numbers',
  cardHolderName: 'cardHolderName',
  expirationMonth: 'expirationMonth',
  expirationYear: 'expirationYear',
  brand: 'brand',
};

const refundTransformer = {
  id: 'refundNumber',
  creditNoteNumber: 'creditNoteNumber',
  currency: 'currency',
  createdDate: 'issuedOn',
  transactionAccountId: 'transactionAccountId',
  memo: 'memo',
  reason: 'reason',
  total: 'amount',
  arn: 'arn',
  requestedBy: 'requestedBy',
  type: 'type',
};

const promotionTransformer = {
  id: 'id',
  title: 'title',
  'applicationReason.readableName': 'applicationReason.readableName',
  'applicationReason.id': 'applicationReason.id',
  status: 'status',
  benefits: {
    key: 'benefits',
    transform: (benefits: any) =>
      benefits &&
      benefits.map((benefit: any) =>
        objectMapper(benefit, {
          duration: 'duration',
          benefitType: 'benefitType',
          subBenefitType: 'subBenefitType',
          value: 'value',
          iterations: 'iterations',
        })
      ),
  },
};

const discountTransformer = {
  amount: 'amount',
  type: 'type',
  percent: 'percent',
  reason: 'reason',
  reasonCode: 'reasonCode',
};

const subscriptionTransformer = {
  id: 'subscriptionId',
  entitlementNumber: 'subscriptionNumber',
  baseProduct: 'baseProduct',
  cloudSiteUrl: 'cloudSiteUrl',
  productId: 'productId',
  billingCycle: 'billingCycle',
  productName: 'productName',
  pricingPlanId: 'pricingPlanId',
  offeringId: 'offeringId',
};

const pricingPlanTransformer = {
  id: 'pricingPlanId',
  type: 'type',
  billingFrequency: 'billingFrequency',
  currency: 'currency',
  pricingPlanType: 'pricingPlanType',
  status: 'status',
  description: 'description',
  tiers: {
    key: 'pricingTiers',
    transform: (tiers: any) =>
      tiers &&
      tiers.map((tier: any) =>
        objectMapper(tier, {
          unitAmount: 'unitAmount',
          flatAmount: 'flatAmount',
          floor: 'floor',
          ceiling: 'ceiling',
        })
      ),
  },
};

const transformPromotionItem = (promotion: any) => {
  return objectMapper(promotion, promotionTransformer);
};

const transformDiscount = (discount: any) => {
  return objectMapper(discount, discountTransformer);
};

const transformSubscription = (subscription: any) => {
  return objectMapper(subscription, subscriptionTransformer);
};

const transformPricingPlan = (pricingPlan: any) => {
  return objectMapper(pricingPlan, pricingPlanTransformer);
};

const invoiceLineItemTransformer = {
  invoiceLineItemId: 'invoiceLineItemId',
  edition: 'edition',
  entitlementId: 'subscriptionId',
  entitlementNumber: 'subscriptionNumber',
  cloudSiteHostname: 'cloudSiteHostname',
  cloudSiteId: 'cloudSiteId',
  productName: 'productName',
  productKey: 'productKey',
  amount: 'amount',
  subtotal: 'subtotal',
  description: 'description',
  startDate: 'billingPeriodStartDate',
  endDate: 'billingPeriodEndDate',
  quantity: 'quantity',
  taxRate: 'taxRate',
  taxAmountPaymentCurrency: 'taxAmountPaymentCurrency',
  promotionItems: {
    key: 'promotionItems',
    transform: (promotionItems: any) =>
      promotionItems && promotionItems.map((promotionItem: any) => transformPromotionItem(promotionItem)),
  },
  discounts: {
    key: 'discounts',
    transform: (discounts: any) => discounts && discounts.map((discount: any) => transformDiscount(discount)),
  },
  entitlement: { key: 'subscription', transform: transformSubscription },
  pricingPlan: { key: 'pricingPlan', transform: transformPricingPlan },
};

const confirmRefundStatus = (refund: any) => (isInvoiceRefunded(refund) ? 'REFUNDED' : 'PENDING');

const getFormattedCommerceAddress = (address: any) => {
  return objectMapper(address, commerceAddressTransformer);
};

const transformCardDetails = (card: CreditCard) => {
  return objectMapper(card, creditCardTransformer);
};

const transformRefund = (refund: any) => {
  return {
    ...objectMapper(refund, refundTransformer),
    status: confirmRefundStatus(refund),
  };
};

const transformInvoicelineItem = (lineItem: any) => {
  return {
    ...objectMapper(lineItem, invoiceLineItemTransformer),
    latestInvoiceDetails: objectMapper(lineItem?.latestInvoiceDetails, {
      id: 'id',
      invoiceNumber: 'invoiceNumber',
    }),
  };
};

const invoiceJsonTransformer = {
  id: 'invoiceId',
  invoiceNumber: 'invoiceNumber',
  status: 'status',
  billTo: { key: 'billToAddress', transform: getFormattedCommerceAddress },
  shipTo: { key: 'soldToAddress', transform: getFormattedCommerceAddress },
  paymentCurrency: 'currency',
  purchaseOrderNumber: 'purchaseOrderNumber',
  total: 'totalAmount',
  subtotal: 'subTotalAmount',
  taxes: 'taxes',
  totalDiscount: 'totalDiscount',
  upgradeCredits: 'upgradeCredits',
  dueDate: 'dueDate',
  createdTimestamp: 'issueDate',
  selfServeBACInvoiceLink: 'billingConsoleInvoiceLink',
  transactionAccountId: 'transactionAccountId',
  invoiceDueDays: 'invoiceDueDays',
  paymentMethod: 'paymentMethod',
  partnerName: 'partnerName',
  refunds: {
    key: 'refunds',
    transform: (refunds: any) => refunds && refunds.map((refund: any) => transformRefund(refund)),
  },
  appliedBalance: 'appliedBalance',
  creditCard: {
    key: 'creditCardDetails',
    transform: transformCardDetails,
  },
  lineItems: {
    key: 'invoiceLineItems',
    transform: (lineItems: any) => lineItems && lineItems.map((lineItem: any) => transformInvoicelineItem(lineItem)),
  },
};

export const transformInvoiceJsonData = (invoice: InvoiceWithLineItems): any => {
  return objectMapper(invoice, invoiceJsonTransformer);
};
